import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X, Download, Smartphone } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Check if already installed
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
    if (isInStandaloneMode) {
      setIsInstalled(true);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Show prompt after some user engagement (3 seconds)
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Salvar preferência por 7 dias
    const dismissedUntil = Date.now() + (7 * 24 * 60 * 60 * 1000);
    localStorage.setItem('pwa-prompt-dismissed-until', dismissedUntil.toString());
  };

  // Don't show if already installed, dismissed, or no prompt available
  if (isInstalled || !showPrompt || (!deferredPrompt && !isIOS)) {
    return null;
  }

  // Verificar se foi dispensado recentemente
  const dismissedUntil = localStorage.getItem('pwa-prompt-dismissed-until');
  if (dismissedUntil && Date.now() < parseInt(dismissedUntil)) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 md:bottom-4 md:left-auto md:right-4 md:max-w-sm">
      <Card className="shadow-lg border-2 border-border bg-background/98 backdrop-blur-md">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-primary/10">
              <Smartphone className="h-5 w-5 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm mb-1 text-foreground">
                Instalar AliMenu
              </h4>
              <p className="text-xs text-foreground/80 mb-3">
                {isIOS 
                  ? 'Adicione à tela inicial para acesso rápido'
                  : 'Instale nosso app para uma experiência melhor'
                }
              </p>
              
              <div className="flex gap-2">
                {isIOS ? (
                  <Button size="sm" variant="default" className="text-xs h-8">
                    <Download className="h-3 w-3 mr-1" />
                    Adicionar
                  </Button>
                ) : (
                  <Button 
                    size="sm" 
                    variant="default" 
                    onClick={handleInstallClick}
                    className="text-xs h-8"
                  >
                    <Download className="h-3 w-3 mr-1" />
                    Instalar
                  </Button>
                )}
                
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={handleDismiss}
                  className="text-xs h-8 px-3 hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              {isIOS && (
                <p className="text-xs text-foreground/70 mt-2">
                  Toque em <span className="font-medium">⤴️</span> e depois em "Adicionar à Tela de Início"
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}